import type { ImageType } from '@eliadmin/core/src/api/api-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { stripTags } from '@eliadmin/core/src/utils/nt/text';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const isSvg = (image: ImageType) => image.url.split('?')[0].toLowerCase().endsWith('.svg');

type CmsImageProps = {
  image: ImageType | null;
  /** Fallback alt when the media has no title (usually the item name, HTML allowed). */
  alt?: string;
  /** Image next to text that already names it (card titles, links): `alt=""`. */
  decorative?: boolean;
  sizes: string;
  /** Optional compression quality for photos that need finer detail at a small size. */
  quality?: number;
  /** Above-the-fold image (LCP). */
  preload?: boolean;
  className?: string;
};

/**
 * Admin photo covering its positioned parent (`object-cover` unless the class says otherwise).
 * Nothing is rendered while the field is empty – the parent keeps its Figma size.
 */
export function CmsImage({
  image,
  alt,
  decorative,
  sizes,
  quality,
  preload,
  className,
}: CmsImageProps) {
  if (!image) return null;

  return (
    <Image
      src={image.url}
      alt={decorative ? '' : resolveImageAlt(image, stripTags(alt ?? ''))}
      fill
      sizes={sizes}
      quality={quality}
      preload={preload}
      fetchPriority={preload ? 'high' : undefined}
      unoptimized={isSvg(image)}
      className={cn('object-cover', className)}
    />
  );
}

/**
 * Content icon from the administration (benefits, menu, problems). The box keeps its Figma size even
 * before the icon is uploaded, so the layout does not move when it arrives.
 */
export function CmsIcon({
  image,
  className,
  sizes = '48px',
}: {
  image: ImageType | null;
  className?: string;
  sizes?: string;
}) {
  return (
    <span aria-hidden="true" className={cn('relative block shrink-0', className)}>
      {image ? (
        <Image
          src={image.url}
          alt=""
          fill
          sizes={sizes}
          unoptimized={isSvg(image)}
          className="object-contain"
        />
      ) : null}
    </span>
  );
}
